import Card from '../../../components/card/Card';
import P from '../../../components/texts/p/P';
import Modal from '../../../components/modal/modal';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import { pageNavigationPlugin } from '@react-pdf-viewer/page-navigation';
import '@react-pdf-viewer/core/lib/styles/index.css';
import * as pdfjsLib from 'pdfjs-dist';
import Button from '../../../components/button/Button';
import Icon from '../../../components/icon/Icon';
import Input from '../../../components/input/Input';
import DragAndDrop from '../../../components/DragAndDrop/DragAndDrop';
import { MAX_FILE_SIZE } from '../../../utils/Constants';
import { useState } from 'react';
import Span from '../../../components/texts/span/Span';
import { useFormik } from 'formik';

interface ManualSignatureModalProps {
  cuit: string | undefined;
  isOpen: boolean;
  onClose: Function;
  onAccept: Function;
}

const ManualSignatureModal: React.FC<ManualSignatureModalProps> = ({ isOpen, onClose, onAccept }) => {
  const zoomPluginInstance = zoomPlugin();
  const pageNavPluginInstance = pageNavigationPlugin();

  const { ZoomInButton, ZoomOutButton } = zoomPluginInstance;
  const { GoToNextPage, GoToPreviousPage } = pageNavPluginInstance;
  const [file, setFile] = useState<Uint8Array | undefined>();
  const [fileObject, setFileObject] = useState<File | undefined>();
  const [multipleFileError, setMultipleFileError] = useState<string>('');

  const validateManualData = (values: any[]) => {
    const errors: any[] = [];
    values.forEach((contract, index) => {
      const contractErrors: any = {};
      if (!contract.book_location) {
        contractErrors.book_location = 'El campo ubicación en libros es obligatorio';
      }
      if (!contract.file) {
        contractErrors.file = 'El campo archivo es obligatorio';
      }
      if (Object.keys(contractErrors).length > 0) {
        errors[index] = contractErrors;
      }
    });
    return errors;
  };

  const manualContractFormik = useFormik({
    initialValues: [
      {
        book_location: '',
        file: '',
      },
    ],
    validate: validateManualData,
    validateOnMount: true,
    onSubmit: (values) => { },
  });

  const handleFile = (file: File) => {
    file.arrayBuffer().then((buffer) => {
      const uint8Array = new Uint8Array(buffer);
      setFile(uint8Array)
    });

    // Guardar el archivo en estado para enviarlo después
    setFileObject(file);
    manualContractFormik.setFieldValue(`[0].file`, file.name);
  };

  const resetManualForm = () => {
    manualContractFormik.resetForm();
    setFile(undefined);
    setFileObject(undefined);
    setMultipleFileError('');
  };

  const handleOnClose = () => {
    resetManualForm();
    onClose();
  };

  const handleOnAccept = () => {
    // Enviar tanto el book_location como el archivo File object
    onAccept({
      book_location: manualContractFormik.values[0].book_location,
      file: fileObject
    });
    resetManualForm();
  };

  const renderContent = () => {
    return (
      <Card>
        <P type='1' variant='neutral' withMargin={false}>
          Este es el contrato que vas a cargar. Asegurate de que todos los datos sean correctos.
        </P>
        <Input
          inputStatus={manualContractFormik.errors[0]?.book_location ? 'error' : 'normal'}
          label='Ubicación en libros'
          placeholder='Escribí una referencia para encontrar el contrato'
          onChange={(e) => manualContractFormik.setFieldValue(`[0].book_location`, e.target.value)}
        />
        <Span variant='danger' type='2'>
          {manualContractFormik.errors[0]?.book_location}
        </Span>
        {file !== undefined ?
          <Card>
            <Card margin={{ top: '3' }} height='40vh'>
              <Worker workerUrl={`https://unpkg.com/pdfjs-dist@${pdfjsLib.version}/build/pdf.worker.min.js`}>
                <Viewer
                  fileUrl={file}
                  plugins={[zoomPluginInstance, pageNavPluginInstance]}
                  defaultScale={1}
                />
              </Worker>
            </Card>
            <Card display='flex' justifyContent='center' gap='8px' variant='neutral' borderRadius={false} spacing="2">
              <GoToPreviousPage>
                {(props) => (
                  <Button onClick={props.onClick} size='sm' variant='neutral'>
                    <Icon name="chevron_left" variant='neutral' height={20} />
                  </Button>
                )}
              </GoToPreviousPage>
              <GoToNextPage>
                {(props) => (
                  <Button onClick={props.onClick} size='sm' variant='neutral'>
                    <Icon name="chevron_right" variant='neutral' height={20} cursor='pointer' />
                  </Button>
                )}
              </GoToNextPage>
              <ZoomOutButton />
              <ZoomInButton />
            </Card>
          </Card>
          :
          <Card margin={{top:'3'}}>
            <DragAndDrop
              name='documents'
              setValue={(file) => handleFile(file)}
              fileTypes={['pdf']}
              maxFileSize={MAX_FILE_SIZE}
              showError={(error: string) => setMultipleFileError(error)}
              formatTextError='El formato de archivo solo puede ser: .pdf'
            />
            {multipleFileError && (
              <Span variant='danger' type='2'>
                {multipleFileError}
              </Span>
            )}
          </Card>
        }
      </Card>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      title={'Confirmar archivo'}
      primaryText='Cargar contrato'
      primaryVariant='primary'
      secondaryText='Cancelar'
      secondaryVariant='secondary'
      onSecondaryAction={handleOnClose}
      onClose={handleOnClose}
      onPrimaryAction={handleOnAccept}
      children={renderContent()}
      width='800px'
      primaryActionDisabled={!manualContractFormik.isValid}
    />
  );
};

export default ManualSignatureModal;